<template>
  <div>
    <div ref="map" style="width:100%;height:340px;border-radius:8px;"></div>
    <p v-if="loadError" class="text-danger" style="margin-top:6px;">
      Map unavailable. You can still save the venue by address.
    </p>
    <p v-else-if="draggable" style="margin-top:6px;font-size:13px;color:#2563eb;font-weight:600;">
      Drag the pin (or click the map) to the exact spot.
    </p>
    <p v-if="coords && !loadError" style="margin-top:4px;font-size:12px;color:#6b7280;">
      {{ coords.lat.toFixed(6) }}, {{ coords.lng.toFixed(6) }}
    </p>
  </div>
</template>

<script>
import { loadGoogleMaps } from '../../utils/googleMaps';

export default {
  name: 'VenuePinMap',
  props: {
    lat: { type: Number, default: null },
    lng: { type: Number, default: null },
    address: { type: String, default: '' },
    draggable: { type: Boolean, default: false },
    zoom: { type: Number, default: 17 },
    // Existing stored source ('manual'|'geocoded') when re-opening a saved pin;
    // preserves a human ('manual') placement through re-confirmation. Null on the
    // add flow (no saved pin yet), where the source is derived from the action.
    source: { type: String, default: null },
  },
  data() {
    return { gmaps: null, map: null, marker: null, coords: null, loadError: false };
  },
  watch: {
    // Parent toggles this on when the user taps "Move pin".
    draggable(val) {
      if (this.marker) this.marker.setDraggable(val);
    },
  },
  async mounted() {
    try {
      this.gmaps = await loadGoogleMaps();
    } catch (e) {
      this.loadError = true;
      return;
    }
    this.initMap();
  },
  methods: {
    initMap() {
      const hasPin = this.lat != null && this.lng != null;
      const start = hasPin ? { lat: this.lat, lng: this.lng } : { lat: 37.7793, lng: -122.4193 };
      this.map = new this.gmaps.Map(this.$refs.map, {
        center: start,
        zoom: hasPin ? this.zoom : 4,
        streetViewControl: false,
        mapTypeControl: false,
      });
      this.marker = new this.gmaps.Marker({ map: this.map, position: start, draggable: this.draggable });

      // Live: dragging the pin or clicking the map is a MANUAL placement.
      this.marker.addListener('dragend', () => this.emitFromMarker('manual'));
      this.map.addListener('click', (e) => {
        if (!this.draggable) return; // only re-place when the user is in "move" mode
        this.marker.setPosition(e.latLng);
        this.emitFromMarker('manual');
      });

      if (!hasPin && this.address) {
        // No existing pin -> geocode the address once to seed the marker (geocoded).
        new this.gmaps.Geocoder().geocode({ address: this.address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            this.map.setCenter(results[0].geometry.location);
            this.map.setZoom(this.zoom);
            this.marker.setPosition(results[0].geometry.location);
            this.emitFromMarker('geocoded');
          }
        });
      } else if (hasPin) {
        // Reflect an existing pin to the parent without changing it. Keep the
        // stored source if we were told one (a saved 'manual' pin stays manual).
        this.emitFromMarker(this.source || (this.draggable ? 'manual' : 'geocoded'));
      }
    },
    emitFromMarker(source) {
      const pos = this.marker.getPosition();
      this.coords = { lat: pos.lat(), lng: pos.lng() };
      this.$emit('update:coords', { lat: this.coords.lat, lng: this.coords.lng, source });
    },
  },
};
</script>
